import { ReactElement, useEffect, useState } from 'react'
import { Form, Field, Formik, FormikProps, FormikErrors, FormikValues, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import {
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUser
} from 'amazon-cognito-identity-js'
import { Button, Input, Text } from '../atoms'
import apis from '../../libs/services'
import { userPool } from '../../userpool'
import { useAuth } from '../../hooks/useAuth'
import { toast } from 'react-toastify'
import Alert from '../../assets/images/alert-red.png'
import { Modal } from '../molecules'
import useSetFocusById from '../../hooks/useSetFocus'
import ControlHighlight from '../molecules/KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../molecules/KeyboardControls/KeyboardControl'
import { useAuthContext } from '../../contexts/AuthContext'
import { logger } from '../../utils'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ContactLinks from '../molecules/ContactLinks/ContactLinks'

interface FormValues {
  password: string
  confirmPassword: string
}

const initialValues = {
  password: '',
  confirmPassword: ''
}

export enum FocusableElement {
  InputPassword = 'input-password',
  InputConfirmPassword = 'input-confirm-password',
  ButtonContinue = 'button-continue',
  LinkContact = 'link-contact',
}

const SignupStudentFormSchema = Yup.object().shape({
  password: Yup.string().required('Password is required!'),
  confirmPassword: Yup.string()
    .required('Passwords do not match')
    .oneOf([Yup.ref('password')], 'Passwords do not match')
})

function SignupStudent (): ReactElement {
  const navigate = useNavigate()
  const { setUserProfile } = useAuth()
  const { signin } = useAuthContext()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [isPasswordReset, setIsPasswordReset] = useState<boolean>(false)
  const keyboardControls = useKeyboardControls()
  const [setFocusById] = useSetFocusById()
  const location = useLocation()

  useEffect(() => {
    const queryString = window.location.href// Full query, e.g., "?email=...&code=4D...&dopage..."
    // Find positions of `code=` and the unique `&dopageCodeTermination`
    if(queryString !== null && queryString !== undefined && queryString.length > 0) {
    const codeStart = queryString.indexOf('code=')
    const terminationStart = queryString.indexOf('&dopageCodeTermination')
    let rawCodeSegment = queryString.substring(
      codeStart + 5, // 5 = length of "code="
      terminationStart
    )
    if (rawCodeSegment !== null && rawCodeSegment !== undefined && rawCodeSegment.length > 0) {
      // Only decode if code is longer than 8 characters
      if (rawCodeSegment.length > 8) {
        try {
          rawCodeSegment = decodeURIComponent(rawCodeSegment)
        } catch (error) {
          logger.error(new Error('Error decoding code segment'))
          toast.error('There was an error interpreting your temporary code', { position: toast.POSITION.TOP_CENTER })
        }
      }
      setCode(rawCodeSegment)
    } else {
      logger.error(new Error('Code not found in URL'))
      toast.error('There was an error interpreting your temporary code: Code not found in URL', {
        position: toast.POSITION.TOP_CENTER
      })
    }
   } else{
    logger.error(new Error('Invalid URL format'))
    toast.error('There was an error interpreting your temporary code: Invalid URL format', {
      position: toast.POSITION.TOP_CENTER
    })
   }
    setFocusById(FocusableElement.InputPassword)
  }, [location, setCode, setFocusById])

  useEffect(() => {
    const resetPasswordParam = searchParams.get('reset_password')
    // Check if the parameter exists at all, not just if it equals 'true'
    const isReset = resetPasswordParam !== null
    setIsPasswordReset(isReset)
  }, [searchParams])

  useEffect(() => {
    const emailParam = String(searchParams.get('email')) ?? ''
    // + character is replaced by space by searchParam. We're putting it back. Can be removed.
    const email = emailParam.trim().replaceAll(' ', '+')
    setEmail(email)
  }, [searchParams])

  const extractResetCodeFromUrl = (): string => {
    try {
      const url = window.location.href
    
      // Check if URL is valid
      if (!url || typeof url !== 'string') {
        logger.error(new Error('Invalid URL format'))
        return ''
      }
    
    // Look for both ?code= and &code=
    let codeStartIndex = url.indexOf('&code=')
    if (codeStartIndex === -1) {
      codeStartIndex = url.indexOf('?code=')
    }
    
    if (codeStartIndex === -1) {
      logger.error(new Error('Code parameter not found in URL'))
      return ''
    }
    
    // Add 6 to skip '?code=' or '&code='
    codeStartIndex += 6
    
    const resetPasswordIndex = url.indexOf('&reset_password')
      if (resetPasswordIndex === -1) { // '&reset_password' wasn't found
        logger.error(new Error('Reset password parameter not found in URL'))
        return ''
      }
    
      // Check if code segment is empty
      const codeSegment = url.substring(codeStartIndex, resetPasswordIndex)
      if (!codeSegment || codeSegment.trim() === '') {
        logger.error(new Error('Empty code segment found in URL'))
        return ''
      }
    
      return codeSegment
    } catch (error) {
      const err = error as Error
      logger.error(err)
      return ''
    }
  }

  const handleSubmit = (values: FormValues, actions: FormikHelpers<FormValues>): void => {
    try {
      // Manually handle submitting state as the Cognito library doesn't support async/await.
      actions.setSubmitting(true)

      const user = new CognitoUser({
        Username: email,
        Pool: userPool
      })
      if (isPasswordReset) {
        const passwordResetCode = extractResetCodeFromUrl()
        if (passwordResetCode === '') {
          toast.error('There was an error interpreting your temporary code', { position: toast.POSITION.TOP_CENTER })
          actions.setSubmitting(false)
          return
        }

        // Add error handling for network issues
        try {
          user.confirmPassword(passwordResetCode, values.password, {
            onSuccess: () => {
              toast.success('Password has been reset successfully', { position: toast.POSITION.TOP_CENTER })
              actions.setSubmitting(false)
              navigate('/login')
            },
            onFailure: (err) => {
              logger.error(err)
              toast.error(err.message || 'Failed to reset password. Please try again.', { position: toast.POSITION.TOP_CENTER })
              actions.setSubmitting(false)
            }
          })
        } catch (error) {
          const err = error as Error
          logger.error(err)
          toast.error('An unexpected error occurred. Please try again later.', { position: toast.POSITION.TOP_CENTER })
          actions.setSubmitting(false)
        }
      } else {
        // Original sign-up flow
        const attributeList = []
        attributeList.push(
          new CognitoUserAttribute({
            Name: 'email',
            Value: email
          })
        )

        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: code
        })

        user.authenticateUser(authDetails, {
          onSuccess: (_result) => {
            actions.setSubmitting(false)
          },
          onFailure: (err) => {
            toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
            actions.setSubmitting(false)
          },
          newPasswordRequired: function (userAttributes, _requiredAttributes) {
            delete userAttributes.email_verified
            delete userAttributes.email
            const newPassword = values.password

            // Handle async code within a self-invoking async function
            const setNewPasswordAndSignIn = async (): Promise<void> => {
              try {
                await new Promise((resolve, reject) => {
                  user.completeNewPasswordChallenge(newPassword, userAttributes, {
                    onSuccess: resolve,
                    onFailure: reject
                  })
                })

                const signInResponse = await signin(email, newPassword)

                if (signInResponse.isSignedIn === true) {
                  const response = await apis.confirmUserActivation()

                  if (response?.data?.data?.user !== null && response?.data?.data?.user !== undefined) {
                    const userProfile = response?.data?.data?.user
                    setUserProfile(userProfile)
                    navigate('/terms-of-use')
                  }
                }
              } catch (error) {
                const err = error as Error
                logger.error(err)
                toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
              }
            }
            void setNewPasswordAndSignIn()
            actions.setSubmitting(false)
          }
        })
      }
    } catch (error) {
      const err = error as Error
      logger.error(err)
      toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
      actions.setSubmitting(false)
    }
  }

  const isPasswordFocused = keyboardControls.isElementFocused(FocusableElement.InputPassword)
  const isConfirmPasswordFocused = keyboardControls.isElementFocused(FocusableElement.InputConfirmPassword)

  const inputPasswordMargins = (errors: FormikErrors<FormikValues>): string => {
    let inputMargins = ''
    const marginsKeyboardControls = keyboardControls.isEnabled ? 'mt-8' : 'mt-1'

    if (errors.password !== undefined && errors.password !== '') {
      inputMargins = `mb-0 ${marginsKeyboardControls}`
    } else {
      inputMargins = `mb-1 ${marginsKeyboardControls}`
    }

    return inputMargins
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={SignupStudentFormSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting }: FormikProps<FormValues>) => (
        <Modal tall>
          <Text
            className='font-bold text-center'
            bold
            size='LG'
            text={isPasswordReset ? 'Reset password' : 'Sign up'}
            type='paragraph'
          />
          <div className='w-full min-h-768:mt-10 min-h-1024:mt-14'>
            <Text
              className='font-bold text-dopage-lightGreen'
              bold
              size='LG'
              text={isPasswordReset ? 'Welcome back!' : 'Welcome to DopaGE!'}
              type='paragraph'
            />
            <div className='flex mt-[16px]'>
              <Text
                className='text-dopage-primary text-center break-all'
                size='LG'
                text={email}
              />
            </div>
            <Form className='flex flex-col justify-between h-[380px] min-h-768:h-[440px] min-h-1024:h-[500px]'>
              <div className='mt-5 min-h-1024:mt-10'>
                <ControlHighlight
                  active={keyboardControls.isEnabled && isPasswordFocused}
                  highlightEffect={HighlightEffect.ButtonHexOutline}
                  data-testid={`highlight-${FocusableElement.InputPassword}`}
                >
                  <label aria-label='password'>
                    <Field name='password'>
                      {({
                        field
                      }: any) => (
                        <KeyboardControl>
                          <Input
                            id={FocusableElement.InputPassword}
                            data-testid={FocusableElement.InputPassword}
                            className='w-full py-[27px] px-[27px] text-[26px] leading-[35px]'
                            type='password'
                            placeholder={isPasswordReset ? 'Reset password' : 'Create password'}
                            {...field}
                          />
                        </KeyboardControl>
                      )}
                    </Field>
                  </label>
                </ControlHighlight>
                {isPasswordFocused && (
                  <div className='w-full text-dopage-disabled'>
                    <ul className={`${inputPasswordMargins(errors)} flex flex-wrap h-[4.5rem] items-center mx-4 justify-between`}>
                      <div className='self-start'>
                        <li>8 characters minimum</li>
                        <li>1+ uppercase letter</li>
                        <li>1+ lowercase letter</li>
                      </div>
                      <div className='self-start'>
                        <li>1+ number</li>
                        <li>1+ special character</li>
                      </div>
                    </ul>
                  </div>)}
                {errors.password !== undefined && errors.password !== '' && touched.password === true
                  ? (
                    <div className='text-[#ff0000] pb-1 min-h-768:pt-0 min-h-768:pb-1' aria-label={`Error message: ${errors.password}`}>{errors.password}</div>
                    )
                  : null}
                <div className='mt-[24px]'>
                  <ControlHighlight
                    active={keyboardControls.isEnabled && isConfirmPasswordFocused}
                    highlightEffect={HighlightEffect.InputOutline}
                    data-testid={`highlight-${FocusableElement.InputConfirmPassword}`}
                  >
                    <label aria-label='confirm password'>
                      <Field name='confirmPassword'>
                        {({
                          field
                        }: any) => (
                          <KeyboardControl>
                            <Input
                              id={FocusableElement.InputConfirmPassword}
                              data-testid={FocusableElement.InputConfirmPassword}
                              className='w-full py-[27px] px-[27px] text-[26px] leading-[35px]'
                              type='password'
                              placeholder='Confirm password'
                              {...field}
                            />
                          </KeyboardControl>
                        )}
                      </Field>
                    </label>
                  </ControlHighlight>
                </div>
                {errors.confirmPassword !== undefined && errors.confirmPassword !== '' && touched.confirmPassword === true
                  ? (
                    <div className={`text-[#f0543c] ${keyboardControls.isEnabled && isConfirmPasswordFocused ? 'pt-6' : 'pt-1'} pb-6 flex`} aria-label={`Error message: ${errors.password as string}`}>
                      <img alt='confirm-password' className='w-5 h-[18px] mr-2 mt-[3px]' src={Alert} />{errors.confirmPassword}
                    </div>
                    )
                  : null}
              </div>
              <ControlHighlight
                active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonContinue)}
                highlightEffect={HighlightEffect.ButtonHexOutline}
                data-testid={`highlight-${FocusableElement.ButtonContinue}`}
              >
                <KeyboardControl>
                  <Button
                    id={FocusableElement.ButtonContinue}
                    data-testid={FocusableElement.ButtonContinue}
                    className='diamond-btn h-[48px] min-h-768:h-[88px] w-full font-bold mt-2 flex justify-center items-center'
                    type='submit'
                    loading={isSubmitting}
                    disabled={isSubmitting || Object.keys(errors).length > 0}
                  >
                    <Text
                      className='text-black text-[26px]'
                      size='BASE'
                      text='Continue'
                      type='span'
                    />
                  </Button>
                </KeyboardControl>
              </ControlHighlight>
              <ContactLinks
                id={FocusableElement.LinkContact}
              />
            </Form>
          </div>
        </Modal>
      )}
    </Formik>
  )
}

export default SignupStudent
